import { useCallback, useEffect, useRef, useState } from 'react';
import { Provider, useSetAtom } from 'jotai';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import { unlockSpeechAudioContext } from '@/speech/speechAudioContext';
import { BookOpenCheck, CheckCircle2, Languages, Lightbulb, Pencil, Play, RotateCcw, Square, X } from 'lucide-react';
import { useMazeRunner } from '@/apps/maze/useMazeRunner';
import { setFlowMotionAtom } from '@/flow/atoms/flowMotionAtom';
import { setFlowSayTextAtom } from '@/flow/atoms/flowSayTextAtom';
import { setFlowTofAtom } from '@/flow/atoms/flowTofAtom';
import { LogPanel } from '@/flow/components/LogPanel';
import { VirtualRobot } from '@/flow/components/VirtualRobot';
import { DEFAULT_EYE_LED_COLOR } from '@/flow/animation/eyeLedState';
import { runFlow } from '@/flow/run';
import { playSimulationSoundEffect } from '@/flow/simulationSoundPlayer';
import type { AppEdge, AppNode, FlowExecutionEvent, NodeKind } from '@/flow/types';
import type { FlowDisplayLanguage } from '@/flow/displayLanguage';
import { LEARNING_LEVELS, LEARNING_PROBLEMS, getLearningProblem } from './learningCatalog';
import { evaluateLearningRun, shouldShowOptimalAlternative, type LearningEvaluation } from './learningEvaluator';
import { createInitialLearningFlow, normalizeLearningFlow } from './learningInitialFlow';
import { getLearningMazeStage } from './learningMazeStages';
import { requiresLearningMaze } from './learningSimulation';
import {
  loadLearningFlow,
  loadLearningProgress,
  recordHintUsed,
  recordProblemAttempt,
  recordProblemCleared,
  resetProblemProgress,
  saveLearningFlow,
  saveLearningProgress,
  type LearningProgress,
} from './learningProgress';
import { LearningCardPalette } from './LearningCardPalette';
import { LearningFlowCanvas } from './LearningFlowCanvas';

export type LearningAppOverlayProps = {
  problemId: string;
  userSub: string;
  displayLanguage?: FlowDisplayLanguage;
  onNavigate: (problemId: string) => void;
  onClose: () => void;
};

type LearningPhase = 'edit' | 'running' | 'result';

export function LearningAppOverlay(props: LearningAppOverlayProps) {
  return <Provider><LearningAppContent {...props} /></Provider>;
}

function LearningAppContent({ problemId, userSub, displayLanguage = 'ja', onNavigate, onClose }: LearningAppOverlayProps) {
  const { playSimulationSpeech, synthesizeSpeech } = useFiloRemote();
  const problem = getLearningProblem(problemId);
  const level = LEARNING_LEVELS.find(({ id }) => id === problem.levelId) ?? LEARNING_LEVELS[0];
  const stage = getLearningMazeStage(problem);
  const requiresMaze = requiresLearningMaze(problem);
  const [phase, setPhase] = useState<LearningPhase>('edit');
  const [selectedDisplayLanguage, setSelectedDisplayLanguage] = useState<FlowDisplayLanguage>(displayLanguage);
  const [logs, setLogs] = useState<string[]>([]);
  const [evaluation, setEvaluation] = useState<LearningEvaluation | null>(null);
  const [showOptimal, setShowOptimal] = useState(false);
  const [addRequestKind, setAddRequestKind] = useState<NodeKind | null>(null);
  const [activeNodeIds, setActiveNodeIds] = useState<Set<string>>(() => new Set());
  const [activeEdgeIds, setActiveEdgeIds] = useState<Set<string>>(() => new Set());
  const [simulationEyeColor, setSimulationEyeColor] = useState(DEFAULT_EYE_LED_COLOR);
  const [flowRevision, setFlowRevision] = useState(0);
  const [initialFlow, setInitialFlow] = useState(() => normalizeLearningFlow(
    loadLearningFlow(sessionStorage, userSub, problem.id) ?? createInitialLearningFlow(problem),
  ));
  const [progress, setProgress] = useState(() => loadLearningProgress(sessionStorage, userSub));
  const progressRef = useRef(progress);
  const flowRef = useRef<{ nodes: AppNode[]; edges: AppEdge[] }>({ nodes: [], edges: [] });
  const abortControllerRef = useRef<AbortController | null>(null);
  const measureTofRef = useRef<(() => number) | null>(null);
  const setFlowMotion = useSetAtom(setFlowMotionAtom);
  const setSayText = useSetAtom(setFlowSayTextAtom);
  const setTofScan = useSetAtom(setFlowTofAtom);
  const updateProgress = useCallback((next: LearningProgress) => {
    progressRef.current = next;
    setProgress(next);
    saveLearningProgress(sessionStorage, userSub, next);
  }, [userSub]);
  const log = useCallback((line: string) => setLogs((current) => [...current, line]), []);
  const setSimulationLightColor = useCallback((displayColor: string) => {
    setSimulationEyeColor(displayColor);
  }, []);
  const { mazeControl, resetPose, pose } = useMazeRunner(stage, () => undefined);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
    setFlowMotion(null);
    setSayText(null);
    setTofScan(null);
    resetPose();
  }, [resetPose, setFlowMotion, setSayText, setTofScan]);

  const onFlowChange = useCallback((nodes: AppNode[], edges: AppEdge[]) => {
    flowRef.current = { nodes, edges };
  }, []);
  const onPersistFlow = useCallback((nodes: AppNode[], edges: AppEdge[]) => {
    saveLearningFlow(sessionStorage, userSub, problem.id, nodes, edges);
  }, [problem.id, userSub]);
  const onNodeHighlight = useCallback((nodeId: string, active: boolean) => {
    setActiveNodeIds((current) => {
      const next = new Set(current);
      if (active) next.add(nodeId); else next.delete(nodeId);
      return next;
    });
  }, []);
  const onEdgeHighlight = useCallback((edgeId: string, active: boolean) => {
    setActiveEdgeIds((current) => {
      const next = new Set(current);
      if (active) next.add(edgeId); else next.delete(edgeId);
      return next;
    });
  }, []);

  const clearRuntime = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setFlowMotion(null);
    setSayText(null);
    setTofScan(null);
    setSimulationEyeColor(DEFAULT_EYE_LED_COLOR);
    setActiveNodeIds(new Set());
    setActiveEdgeIds(new Set());
    resetPose();
  }, [resetPose, setFlowMotion, setSayText, setTofScan]);

  const startRunning = useCallback(async () => {
    unlockSpeechAudioContext();
    clearRuntime();
    setLogs([]);
    setEvaluation(null);
    setShowOptimal(false);
    setPhase('running');
    const attempted = recordProblemAttempt(progressRef.current, problem.id);
    updateProgress(attempted);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const events: FlowExecutionEvent[] = [];
    await runFlow(
      flowRef.current.nodes,
      flowRef.current.edges,
      log,
      {
        mazeControl: requiresMaze ? { ...mazeControl, continueFlowAfterGoal: true } : undefined,
        setFlowMotion,
        setSayText,
        playSimulationSound: playSimulationSoundEffect,
        playSimulationSpeech,
        synthesizeSpeech,
        setSimulationLightColor,
        setTofScan,
        measureSimulationTof: requiresMaze
          ? () => measureTofRef.current?.() || mazeControl.measureDistanceMm?.(2500) || 0
          : undefined,
        onExecutionEvent: (event) => events.push(event),
      },
      onEdgeHighlight,
      controller.signal,
      false,
      onNodeHighlight,
      true,
    );
    if (controller.signal.aborted) return;
    const result = evaluateLearningRun(problem, flowRef.current.nodes, events);
    if (result.status === 'correct') {
      updateProgress(recordProblemCleared(
        progressRef.current,
        problem.id,
        level.id,
        level.problems.map(({ id }) => id),
      ));
      setShowOptimal(shouldShowOptimalAlternative(problem, flowRef.current.nodes, events));
    }
    setEvaluation(result);
    setPhase('result');
  }, [clearRuntime, level, log, mazeControl, onEdgeHighlight, onNodeHighlight, playSimulationSpeech, synthesizeSpeech, problem, requiresMaze, setFlowMotion, setSayText, setSimulationLightColor, setTofScan, updateProgress]);

  const backToEdit = useCallback(() => {
    clearRuntime();
    setPhase('edit');
  }, [clearRuntime]);
  const showNextHint = () => {
    const usedCount = progress.problems[problem.id]?.hintIdsUsed.length ?? 0;
    if (usedCount >= problem.hints.length) return;
    updateProgress(recordHintUsed(progressRef.current, problem.id, `hint-${usedCount + 1}`));
  };
  const resetFlow = () => {
    clearRuntime();
    const next = resetProblemProgress(sessionStorage, userSub, progressRef.current, problem.id, level.id);
    updateProgress(next);
    setLogs([]);
    setEvaluation(null);
    setPhase('edit');
    setInitialFlow(createInitialLearningFlow(problem));
    setFlowRevision((current) => current + 1);
  };
  const nextProblem = LEARNING_PROBLEMS[LEARNING_PROBLEMS.findIndex(({ id }) => id === problem.id) + 1];
  const usedHints = progress.problems[problem.id]?.hintIdsUsed.length ?? 0;

  return (
    <div className="learning-app-overlay" role="dialog" aria-modal="true" aria-labelledby="learning-app-title">
      <header className="learning-app-overlay__header">
        <div className="learning-app-overlay__heading">
          <BookOpenCheck aria-hidden="true" size={22} />
          <div><p>{level.title}</p><h2 id="learning-app-title">{problem.title}</h2></div>
        </div>
        <div className="learning-app-overlay__actions">
          {phase === 'edit' && <button type="button" className="learning-run-button" onClick={() => void startRunning()}><Play size={15} fill="currentColor" />実行</button>}
          <button
            type="button"
            className="maze-app-language-button"
            onClick={() => setSelectedDisplayLanguage((language) => language === 'ja' ? 'en' : 'ja')}
            aria-label={`表示言語を${selectedDisplayLanguage === 'ja' ? 'English' : '日本語'}に切り替え`}
            title={selectedDisplayLanguage === 'ja' ? 'Switch to English' : '日本語表示に切り替え'}
          >
            <Languages aria-hidden="true" size={15} />
            {selectedDisplayLanguage === 'ja' ? 'EN' : '日本語'}
          </button>
          {phase === 'running' && <button type="button" className="learning-stop-button" onClick={backToEdit}><Square size={14} fill="currentColor" />停止</button>}
          {phase === 'result' && <button type="button" className="learning-run-button" onClick={() => void startRunning()}><RotateCcw size={15} />もう一度</button>}
          {phase !== 'edit' && <button type="button" className="learning-edit-button" onClick={backToEdit}><Pencil size={15} />編集</button>}
          <button type="button" className="chat-app-icon-button" onClick={onClose} aria-label="ウィーゴを動かそうを閉じる"><X size={20} /></button>
        </div>
      </header>
      <div className="learning-app-overlay__body">
        <aside className="learning-app-overlay__problem">
          <p className="learning-problem__description">{problem.description}</p>
          <section className="learning-hints">
            <h3><Lightbulb size={16} />ヒント</h3>
            {problem.hints.slice(0, usedHints).map((hint, index) => <p key={hint}><b>{index + 1}</b>{hint}</p>)}
            <button type="button" disabled={usedHints >= problem.hints.length} onClick={showNextHint}>次のヒントを見る</button>
          </section>
          {evaluation && (
            <section className={`learning-result learning-result--${evaluation.status}`} aria-live="polite">
              <h3>{evaluation.status === 'correct' ? <><CheckCircle2 size={18} />クリア！</> : 'もう少し'}</h3>
              {evaluation.status === 'correct' ? <p>{problem.successMessage}</p> : evaluation.feedback.map((item) => <p key={item}>{item}</p>)}
              {showOptimal && <p className="learning-result__optimal"><b>別の組み方</b>{problem.optimalSolutions[0].explanation}</p>}
              {evaluation.status === 'correct' && nextProblem && <button type="button" onClick={() => onNavigate(nextProblem.id)}>次の問題へ</button>}
            </section>
          )}
          <button type="button" className="learning-reset-button" onClick={resetFlow}>この問題をリセット</button>
        </aside>
        <main className={`learning-app-overlay__workspace${phase === 'edit' ? ' is-editing' : ''}`}>
          <div className="learning-app-overlay__canvas">
            <LearningFlowCanvas
              displayLanguage={selectedDisplayLanguage}
              key={`${problem.id}-${flowRevision}`}
              stageId={stage.id}
              initialFlow={initialFlow}
              availableNodeKinds={problem.availableNodeKinds}
              addRequestKind={addRequestKind}
              onAddHandled={() => setAddRequestKind(null)}
              onFlowChange={onFlowChange}
              onPersistFlow={onPersistFlow}
              activeNodeIds={activeNodeIds}
              activeEdgeIds={activeEdgeIds}
            />
          </div>
          {phase === 'edit' && (
            <section className="maze-card-dock learning-app-overlay__card-dock" aria-label="使えるカード">
              <LearningCardPalette
                availableNodeKinds={problem.availableNodeKinds}
                displayLanguage={selectedDisplayLanguage}
                onTapAdd={setAddRequestKind}
              />
            </section>
          )}
        </main>
        <aside className="learning-app-overlay__tools">
          <section className="learning-app-overlay__simulator" aria-label="シミュレーター">
            {requiresMaze ? (
              <VirtualRobot
                mazeStage={stage}
                mazeRobotPose={pose}
                eyeLedColor={simulationEyeColor}
                topView
                onTofMeasureReady={(measure) => { measureTofRef.current = measure; }}
              />
            ) : <VirtualRobot eyeLedColor={simulationEyeColor} />}
          </section>
          <section className="learning-app-overlay__logs" aria-label="ログ">
            <LogPanel logs={logs} />
          </section>
        </aside>
      </div>
    </div>
  );
}